// @ts-nocheck
/**
 * @fileoverview VAT/Tax Configuration and Display Logic - Theme Version
 * @description Provides tax configuration, calculation, translation and
 *              DOM rendering for VAT/GST display on product pages and
 *              cart drawer. Runs as a plain browser script via script tag.
 *              No export statements - all functions are scoped within
 *              the vatDisplayInitialized guard.
 *
 * Supported markets:
 *   AU - Australia        GST 10% (display enabled)
 *   GB - United Kingdom          (display disabled)
 *   SG - Singapore        GST  9% (display enabled)
 *   TW - Taiwan           VAT  5% (display enabled)
 *   ID - Indonesia        VAT 11% (display enabled)
 *   HK - Hong Kong        0% rate (display disabled)
 *   MY - Malaysia         0% rate (display disabled)
 *   CA - Canada                   (display disabled)
 *   US - United States            (display disabled)
 *
 * BEM Component: .vat-display
 *   Elements:  .vat-display__text
 *              .vat-display__icon
 *              .vat-display__tooltip
 *   Modifiers: .vat-display--cart
 *              .vat-display__tooltip--visible
 *
 * @version 1.0.0
 * @ticket AI-112789
 */

if (typeof window.vatDisplayInitialized === "undefined") {
  window.vatDisplayInitialized = true;

  // ---------------------------------------------------
  // Tax Configuration
  // ---------------------------------------------------

  /**
   * @typedef {Object} TaxConfigEntry
   * @property {boolean}      tax_included   - Whether tax is included in price
   * @property {number|null}  rate           - Tax rate as decimal e.g. 0.10
   * @property {string|null}  tax_name       - Short tax name e.g. 'GST'
   * @property {string|null}  [tax_fullname] - Full tax name for tooltip
   * @property {string|null}  tooltip        - Tooltip explanation text
   */

  /**
   * Tax configuration map keyed by ISO 3166-1 alpha-2 country code.
   * Countries with tax_included: false will not display any tax information.
   * @type {Object.<string, TaxConfigEntry>}
   * @constant
   */
  const TAX_CONFIG = {
    // --- TAX INCLUSIVE - WILL DISPLAY ---
    AU: {
      tax_included: true,
      rate: 0.1,
      tax_name: "GST",
      tax_fullname: "Goods and Services Tax",
      tooltip: "The GST (Goods and Services Tax) amount is a government-imposed tax included in the price.",
    },
    GB: { tax_included: false, rate: null, tax_name: null, tooltip: null },
    SG: {
      tax_included: true,
      rate: 0.09,
      tax_name: "GST",
      tax_fullname: "Goods and Services Tax",
      tooltip: "The GST (Goods and Services Tax) amount is a government-imposed tax included in the price.",
    },
    TW: {
      tax_included: true,
      rate: 0.05,
      tax_name: "VAT",
      tax_fullname: "Value Added Tax",
      tooltip: "The VAT (Value Added Tax) amount is a government-imposed tax included in the price.",
    },
    ID: {
      tax_included: true,
      rate: 0.11,
      tax_name: "VAT",
      tax_fullname: "Value Added Tax",
      tooltip: "The VAT (Value Added Tax) amount is a government-imposed tax included in the price.",
    },

    // --- TAX INCLUSIVE BUT ZERO RATE - NO DISPLAY ---
    HK: { tax_included: false, rate: null, tax_name: null, tooltip: null },
    MY: { tax_included: false, rate: null, tax_name: null, tooltip: null },

    // --- TAX EXCLUSIVE - NO DISPLAY ---
    CA: { tax_included: false, rate: null, tax_name: null, tooltip: null },
    US: { tax_included: false, rate: null, tax_name: null, tooltip: null },
  };

  // ---------------------------------------------------
  // Translations
  // ---------------------------------------------------

  /**
   * @typedef {Object} TranslationEntry
   * @property {string} includes_tax       - Product page tax-inclusive label
   * @property {string} cart_tax_total     - Cart total tax-inclusive label
   * @property {string} tax_label          - Short tax label with rate
   * @property {string} checkout_inclusive - Checkout page tax-inclusive label
   */

  /**
   * Translation strings keyed by locale code.
   * Supports exact locale match (e.g. 'en-AU') with fallback
   * to language-only match (e.g. 'en') then to 'en' default.
   *
   * Token replacements:
   *   {tax_name} - replaced with tax name e.g. GST, VAT
   *   {rate}     - replaced with rate percentage e.g. 10
   *   {amount}   - replaced with formatted currency e.g. A$6.55
   *
   * @type {Object.<string, TranslationEntry>}
   * @constant
   */
  const TRANSLATIONS = {
    en: {
      includes_tax: "{tax_name} of {amount} included",
      includes_tax_range: "{tax_name} of {amount_min} – {amount_max} included",
      cart_tax_total: "{tax_name} of {amount} included",
      tax_label: "{tax_name} ({rate}%)",
      checkout_inclusive: "Product price and shipping is {tax_name} inclusive {amount}",
    },
    "en-AU": {
      includes_tax: "{tax_name} of {amount} included",
      includes_tax_range: "{tax_name} of {amount_min} – {amount_max} included",
      cart_tax_total: "{tax_name} of {amount} included",
      tax_label: "{tax_name} ({rate}%)",
      checkout_inclusive: "Product price and shipping is {tax_name} inclusive {amount}",
    },
    "en-GB": {
      includes_tax: "Includes {tax_name} of {amount}",
      includes_tax_range: "Includes {tax_name} of {amount_min} – {amount_max}",
      cart_tax_total: "Includes {tax_name} of {amount}",
      tax_label: "{tax_name} ({rate}%)",
      checkout_inclusive: "Product price and shipping is {tax_name} inclusive {amount}",
    },
    "en-SG": {
      includes_tax: "{tax_name} of {amount} included",
      includes_tax_range: "{tax_name} of {amount_min} – {amount_max} included",
      cart_tax_total: "{tax_name} of {amount} included",
      tax_label: "{tax_name} ({rate}%)",
      checkout_inclusive: "Product price and shipping is {tax_name} inclusive {amount}",
    },
    "en-TW": {
      includes_tax: "{tax_name} of {amount} included",
      includes_tax_range: "{tax_name} of {amount_min} – {amount_max} included",
      cart_tax_total: "{tax_name} of {amount} included",
      tax_label: "{tax_name} ({rate}%)",
      checkout_inclusive: "Product price and shipping is {tax_name} inclusive {amount}",
    },
    "en-ID": {
      includes_tax: "{tax_name} of {amount} included",
      includes_tax_range: "{tax_name} of {amount_min} – {amount_max} included",
      cart_tax_total: "{tax_name} of {amount} included",
      tax_label: "{tax_name} ({rate}%)",
      checkout_inclusive: "Product price and shipping is {tax_name} inclusive {amount}",
    },
    fr: {
      includes_tax: "{tax_name} de {amount} inclus",
      includes_tax_range: "{tax_name} de {amount_min} – {amount_max} inclus",
      cart_tax_total: "{tax_name} de {amount} inclus",
      tax_label: "{tax_name} ({rate}%)",
      checkout_inclusive: "Le prix du produit et la livraison sont {tax_name} inclus {amount}",
    },
    zh: {
      includes_tax: "已含 {tax_name} {amount}",
      includes_tax_range: "已含 {tax_name} {amount_min} – {amount_max}",
      cart_tax_total: "已含 {tax_name} {amount}",
      tax_label: "{tax_name}（{rate}%）",
      checkout_inclusive: "商品價格及運費已含 {tax_name} {amount}",
    },
    "zh-TW": {
      includes_tax: "已含 {tax_name} {amount}",
      includes_tax_range: "已含 {tax_name} {amount_min} – {amount_max}",
      cart_tax_total: "已含 {tax_name} {amount}",
      tax_label: "{tax_name}（{rate}%）",
      checkout_inclusive: "商品價格及運費已含 {tax_name} {amount}",
    },
    id: {
      includes_tax: "Sudah termasuk {tax_name} sebesar {amount}",
      includes_tax_range: "Sudah termasuk {tax_name} sebesar {amount_min} – {amount_max}",
      cart_tax_total: "Sudah termasuk {tax_name} sebesar {amount}",
      tax_label: "{tax_name} ({rate}%)",
      checkout_inclusive: "Harga produk dan pengiriman sudah termasuk {tax_name} {amount}",
    },
    "id-ID": {
      includes_tax: "Sudah termasuk {tax_name} sebesar {amount}",
      includes_tax_range: "Sudah termasuk {tax_name} sebesar {amount_min} – {amount_max}",
      cart_tax_total: "Sudah termasuk {tax_name} sebesar {amount}",
      tax_label: "{tax_name} ({rate}%)",
      checkout_inclusive: "Harga produk dan pengiriman sudah termasuk {tax_name} {amount}",
    },
  };

  // ---------------------------------------------------
  // Currency Symbols
  // ---------------------------------------------------

  /**
   * Currency symbol map keyed by ISO 4217 currency code.
   * Used to ensure consistent symbol display across locale
   * and currency combinations where Intl.NumberFormat
   * returns inconsistent results.
   * @type {Object.<string, string>}
   * @constant
   */
  const CURRENCY_SYMBOLS = {
    AUD: "A$",
    GBP: "£",
    SGD: "S$",
    TWD: "NT$",
    IDR: "Rp",
    CAD: "CA$",
    HKD: "HK$",
    MYR: "RM",
    USD: "$",
    EUR: "€",
  };

  // ---------------------------------------------------
  // Icons
  // ---------------------------------------------------

  /**
   * Inline info icon: a solid disc (currentColor) with a white "i", matching
   * the Figma design. The "i" is drawn explicitly in white (rather than relying
   * on a fill-rule knockout) so it renders reliably across browsers. Replaces
   * the previous ⓘ unicode glyph and the thin outline variant, which looked
   * faint/cut off at small sizes.
   * @constant {string}
   */
  const INFO_ICON_SVG =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
    '<circle cx="12" cy="12" r="10" fill="currentColor"></circle>' +
    '<rect x="11" y="10.5" width="2" height="6.5" rx="1" fill="#fff"></rect>' +
    '<circle cx="12" cy="7.5" r="1.3" fill="#fff"></circle></svg>';

  /**
   * Escapes HTML-sensitive characters in a string before it is interpolated
   * into markup built via innerHTML/string concatenation (e.g. the tooltip
   * text, which now comes from a translated locale value read out of a
   * dataset attribute and could contain quotes or other markup-breaking
   * characters).
   *
   * @param {string} value - Raw string to escape
   * @returns {string} HTML-escaped string
   */
  function escapeHtml(value) {
    if (typeof value !== "string") {
      return "";
    }
    return value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  // ---------------------------------------------------
  // Core Functions
  // ---------------------------------------------------

  /**
   * Retrieves the tax configuration for a given country code.
   * Performs case-insensitive lookup against TAX_CONFIG.
   *
   * @param {string} countryCode - ISO 3166-1 alpha-2 country code e.g. 'AU'
   * @returns {TaxConfigEntry|null} Tax config entry or null if not found
   *
   * @example
   * getTaxConfig('AU');
   * // Returns { tax_included: true, rate: 0.10, tax_name: 'GST', ... }
   */
  function getTaxConfig(countryCode) {
    if (!countryCode) {
      return null;
    }
    return TAX_CONFIG[countryCode.toUpperCase()] || null;
  }

  /**
   * Extracts the tax amount from a tax-inclusive price.
   * Uses the formula: Tax = Price - (Price / (1 + rate))
   *
   * @param {number} priceInCents - Price in smallest currency unit
   * @param {number} rate         - Tax rate as decimal e.g. 0.10 for 10%
   * @returns {number} Tax amount in cents rounded to nearest integer
   *
   * @example
   * extractTaxFromPrice(6550, 0.10);
   * // Returns 595
   */
  function extractTaxFromPrice(priceInCents, rate) {
    if (!priceInCents || !rate) {
      return 0;
    }
    const priceExTax = priceInCents / (1 + rate);
    return Math.round(priceInCents - priceExTax);
  }

  /**
   * @typedef {Object} DisplayTaxResult
   * @property {boolean}  shouldDisplay - Whether tax should be displayed
   * @property {number}   [taxAmount]   - Tax amount in cents
   * @property {string}   [taxName]     - Short tax name e.g. 'GST'
   * @property {string}   [taxFullName] - Full tax name
   * @property {string}   [tooltip]     - Tooltip explanation text
   * @property {number}   [rate]        - Tax rate as decimal e.g. 0.10
   * @property {number}   [ratePercent] - Tax rate as percentage e.g. 10
   */

  /**
   * Determines whether tax should be displayed and calculates the amount.
   * Returns shouldDisplay: false for tax-exclusive countries or unknown
   * country codes.
   *
   * @param {number} priceInCents - Price in smallest currency unit
   * @param {string} countryCode  - ISO 3166-1 alpha-2 country code
   * @returns {DisplayTaxResult} Display tax result object
   *
   * @example
   * getDisplayTax(10000, 'AU');
   * // Returns { shouldDisplay: true, taxAmount: 909, taxName: 'GST', ... }
   *
   * getDisplayTax(10000, 'US');
   * // Returns { shouldDisplay: false }
   */
  function getDisplayTax(priceInCents, countryCode) {
    const config = getTaxConfig(countryCode);
    if (!config || !config.tax_included || !config.rate) {
      return { shouldDisplay: false };
    }
    return {
      shouldDisplay: true,
      taxAmount: extractTaxFromPrice(priceInCents, config.rate),
      taxName: config.tax_name,
      taxFullName: config.tax_fullname,
      tooltip: config.tooltip,
      rate: config.rate,
      ratePercent: Math.round(config.rate * 100),
    };
  }

  /**
   * Retrieves translation strings for a given locale.
   * Uses a fallback chain:
   *   1. Exact locale match    e.g. 'en-AU'
   *   2. Language-only match   e.g. 'en'
   *   3. Default English       'en'
   *
   * @param {string} locale - Locale code e.g. 'en-AU', 'zh', 'fr'
   * @returns {TranslationEntry} Translation strings for the locale
   *
   * @example
   * getTranslation('en-AU');
   * // Returns en-AU specific translations
   *
   * getTranslation('ja');
   * // Falls back to 'en' default translations
   */
  function getTranslation(locale) {
    if (!locale) {
      return TRANSLATIONS["en"];
    }
    if (TRANSLATIONS[locale]) {
      return TRANSLATIONS[locale];
    }
    const languageCode = locale.split("-")[0];
    if (TRANSLATIONS[languageCode]) {
      return TRANSLATIONS[languageCode];
    }
    return TRANSLATIONS["en"];
  }

  /**
   * Replaces named tokens in a translation template string.
   * Tokens use the format {tokenName}.
   * Unmatched tokens are left as-is.
   *
   * @param {string} template - Template string with {token} placeholders
   * @param {Object} values   - Key/value pairs to replace tokens with
   * @returns {string} Completed string with tokens replaced
   *
   * @example
   * applyTranslation('GST of {amount} included', { amount: 'A$6.55' });
   * // Returns 'GST of A$6.55 included'
   */
  function applyTranslation(template, values) {
    if (typeof template !== "string") {
      return "";
    }
    return template.replace(/\{(\w+)\}/g, (match, key) => {
      return values.hasOwnProperty(key) ? values[key] : match;
    });
  }

  /**
   * Formats a cents amount using the shop's Liquid money format string
   * (e.g. "AUD${{amount}}"), so client-rendered tax amounts match the
   * server-rendered prices around them exactly — same symbol, same
   * separators, per store.
   *
   * Supported placeholders:
   *   amount                                   1,134.65
   *   amount_no_decimals                       1,135
   *   amount_with_comma_separator              1.134,65
   *   amount_no_decimals_with_comma_separator  1.135
   *   amount_with_apostrophe_separator         1'134.65
   *
   * @param {number} amountInCents - Amount in smallest currency unit
   * @param {string} moneyFormat   - Shop money format string
   * @returns {string} Formatted currency string e.g. 'AUD$6.55'
   */
  function formatMoneyWithShopFormat(amountInCents, moneyFormat) {
    function formatDelimited(cents, precision, thousands, decimal) {
      const fixed = (cents / 100).toFixed(precision);
      const parts = fixed.split(".");
      const integer = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? integer + decimal + parts[1] : integer;
    }
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, placeholder) {
      switch (placeholder) {
        case "amount":
          return formatDelimited(amountInCents, 2, ",", ".");
        case "amount_no_decimals":
          return formatDelimited(amountInCents, 0, ",", ".");
        case "amount_with_comma_separator":
          return formatDelimited(amountInCents, 2, ".", ",");
        case "amount_no_decimals_with_comma_separator":
          return formatDelimited(amountInCents, 0, ".", ",");
        case "amount_with_apostrophe_separator":
          return formatDelimited(amountInCents, 2, "'", ".");
        default:
          return match;
      }
    });
  }

  /**
   * Formats a cents amount as a localised currency string.
   * Fallback for blocks rendered without a money format attribute.
   * Uses a hardcoded symbol map for consistent symbol display.
   *
   * @param {number} amountInCents - Amount in smallest currency unit
   * @param {string} currencyCode  - ISO 4217 currency code e.g. 'AUD'
   * @param {string} locale        - Locale for number formatting e.g. 'en-AU'
   * @returns {string} Formatted currency string e.g. 'A$6.55'
   *
   * @example
   * formatCurrency(655, 'AUD', 'en-AU');
   * // Returns 'A$6.55'
   */
  function formatCurrency(amountInCents, currencyCode, locale) {
    const amount = amountInCents / 100;
    if (!currencyCode) {
      return amount.toFixed(2);
    }
    const symbol = CURRENCY_SYMBOLS[currencyCode] || currencyCode;
    try {
      const numberPart = new Intl.NumberFormat(locale || "en", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
      return `${symbol}${numberPart}`;
    } catch (e) {
      console.warn("VAT Display: Could not format number", e);
      return `${symbol}${amount.toFixed(2)}`;
    }
  }

  // ---------------------------------------------------
  // Tooltip
  // ---------------------------------------------------

  /**
   * Initialises tooltip show/hide behaviour for a VAT display block.
   * Click/tap only (MA-2196) — hover and focus were dropped because a tap's
   * synthetic focus-before-click made the icon look unclickable on touch
   * devices, and hover-vs-click together produced the same kind of flicker.
   *
   * @param {HTMLElement} block - The VAT display block DOM element
   * @returns {void}
   */
  function initTooltip(block) {
    const info = block.querySelector(".vat-display__info");
    const icon = block.querySelector(".vat-display__icon");
    const tooltip = block.querySelector(".vat-display__tooltip");
    if (!info || !icon || !tooltip) {
      return;
    }

    const GAP = 8;

    // Position the tooltip in viewport coordinates derived from the icon's rect.
    // The tooltip is portaled out of its original parent (see show) so NO
    // ancestor overflow:hidden or transform can clip it — the buybox was
    // cutting off the bottom line.
    // Flips above the icon when there's no room below. Anchored horizontally
    // by `right` (distance from the icon's right edge to the viewport's right
    // edge) instead of `left`, so the tooltip grows leftward into the drawer
    // rather than rightward — clamped to the cart drawer's content column
    // when the block lives inside one, not the full viewport, since the
    // drawer is a narrow panel anchored to the right on wide screens
    // (MA-2196).
    const positionTooltip = () => {
      const r = icon.getBoundingClientRect();
      tooltip.classList.remove("vat-display__tooltip--above");
      tooltip.style.position = "fixed";
      tooltip.style.left = "auto";
      tooltip.style.right = "0px";
      tooltip.style.top = "0px";
      const tw = tooltip.offsetWidth;
      const th = tooltip.offsetHeight;

      const cartItems = block.closest(".cart-items-component");
      const bounds = cartItems ? cartItems.getBoundingClientRect() : null;
      const rightEdge = bounds ? bounds.right : window.innerWidth;
      const leftEdge = bounds ? bounds.left : 0;

      let right = window.innerWidth - r.right;
      const minRight = window.innerWidth - rightEdge;
      const maxRight = window.innerWidth - leftEdge - tw - 8;
      right = Math.min(Math.max(right, minRight), Math.max(minRight, maxRight));

      let top = r.bottom + GAP;
      if (top + th > window.innerHeight - 8 && r.top - GAP - th > 8) {
        top = r.top - GAP - th;
        tooltip.classList.add("vat-display__tooltip--above");
      }
      tooltip.style.right = `${right}px`;
      tooltip.style.top = `${top}px`;
    };

    const onReposition = () => {
      if (tooltip.classList.contains("vat-display__tooltip--visible")) {
        positionTooltip();
      }
    };

    const show = () => {
      // Portal out of the buybox/cart markup so no ancestor overflow/transform
      // can clip the tooltip. When the block lives inside an open native
      // <dialog> (the cart drawer), portal into that dialog instead of
      // <body>: a shown <dialog> is promoted to the browser's top layer,
      // which always paints above regular DOM regardless of z-index, so a
      // tooltip appended to <body> would stay hidden behind it no matter how
      // high its z-index goes (MA-2196). Appending inside the dialog puts the
      // tooltip in that same top-layer subtree.
      const openDialog = block.closest("dialog[open]");
      const host = openDialog || document.body;
      if (tooltip.parentElement !== host) {
        host.appendChild(tooltip);
      }
      tooltip.classList.add("vat-display__tooltip--visible");
      icon.setAttribute("aria-expanded", "true");
      positionTooltip();
      window.addEventListener("scroll", onReposition, true);
      window.addEventListener("resize", onReposition);
    };
    const hide = () => {
      tooltip.classList.remove("vat-display__tooltip--visible");
      icon.setAttribute("aria-expanded", "false");
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
    const toggle = () => (tooltip.classList.contains("vat-display__tooltip--visible") ? hide() : show());

    // Click/tap toggles — the only trigger (MA-2196). stopPropagation keeps
    // the document handler below from immediately re-hiding it on the same
    // event.
    icon.addEventListener("click", (e) => {
      e.stopPropagation();
      toggle();
    });

    // Dismiss when tapping/clicking outside both the icon and the tooltip.
    document.addEventListener("click", (e) => {
      if (!info.contains(e.target) && !tooltip.contains(e.target)) {
        hide();
      }
    });
  }

  // ---------------------------------------------------
  // Block Renderer
  // ---------------------------------------------------

  /**
   * Renders VAT blocks and retries if cart drawer block is still empty.
   * Handles race conditions where drawer content loads asynchronously.
   *
   * @param {number} [retries=3] - Number of retry attempts remaining
   * @param {number} [delay=300] - Delay between retries in milliseconds
   * @returns {void}
   */
  function renderWithRetry(retries, delay) {
    retries = retries || 3;
    delay = delay || 300;

    window.renderVatDisplayBlocks();

    const drawerBlock = document.getElementById("vat-display-cart-cart-drawer");
    const cartPageBlock = document.getElementById("vat-display-cart-page-summary");
    const drawerEmpty = drawerBlock !== null && drawerBlock.innerHTML.trim() === "";
    const cartPageEmpty = cartPageBlock !== null && cartPageBlock.innerHTML.trim() === "";
    if ((drawerEmpty || cartPageEmpty) && retries > 0) {
      setTimeout(function () {
        renderWithRetry(retries - 1, delay);
      }, delay);
    }
  }

  /**
   * Renders the VAT display block with calculated tax amount and tooltip.
   * Updates the innerHTML of the block element with formatted tax info.
   * Hides the block via display:none for tax-exclusive countries.
   *
   * @param {HTMLElement} block - The VAT display block DOM element
   * @returns {void}
   */
  function renderBlock(block) {
    const countryCode = block.dataset.country;
    const priceInCents = parseInt(block.dataset.price, 10);
    const currencyCode = block.dataset.currency;
    const locale = block.dataset.locale;
    const context = block.dataset.context || "product";

    // --- Price-range detection ---
    // When both data-price-min and data-price-max are present, valid, and
    // differ, render a tax-amount range instead of a single amount.
    const priceMinRaw = block.dataset.priceMin;
    const priceMaxRaw = block.dataset.priceMax;
    const priceMin = priceMinRaw ? parseInt(priceMinRaw, 10) : 0;
    const priceMax = priceMaxRaw ? parseInt(priceMaxRaw, 10) : 0;
    const isRange = priceMin > 0 && priceMax > 0 && priceMin !== priceMax;

    // Use the range min for the shouldDisplay check (any bound works — same
    // country/rate), falling back to the single-price value.
    const checkPrice = isRange ? priceMin : priceInCents;
    const config = getTaxConfig(countryCode);
    // The PDP UC wrapper ([data-rewards-uc-vat]) ships hidden and owns the flex
    // gap slot between the UC and SRP rows; toggle it with the same decision so a
    // non-GST country never keeps an empty block there (MA-1422 QA feedback).
    // closest() is null outside the PDP UC context (cart drawer, cart page).
    const ucWrapper = block.closest("[data-rewards-uc-vat]");
    if (!config || !config.tax_included || !config.rate) {
      block.style.display = "none";
      if (ucWrapper) ucWrapper.setAttribute("hidden", "");
      return;
    }

    block.style.display = "";
    if (ucWrapper) ucWrapper.removeAttribute("hidden");

    // Prefer the shop's own money format so the amount matches the
    // server-rendered prices around it (symbol and separators per store);
    // the symbol-map formatter remains as a fallback. Applied across every
    // output path below (cart-page row, range min/max, single amount).
    const moneyFormat = block.dataset.moneyFormat;
    const formatTax = (cents) => {
      return moneyFormat ? formatMoneyWithShopFormat(cents, moneyFormat) : formatCurrency(cents, currencyCode, locale);
    };

    // Wrap the formatted tax amount so it can be bolded to match Figma.
    const boldAmount = (formatted) => `<strong class="vat-display__amount">${formatted}</strong>`;

    // Cart page order summary: a label/value row (e.g. "VAT:   Rp2.973")
    // matching the sibling rows (Total Retail, UF Cost, ...), without the
    // sentence + tooltip treatment used on the PDP and cart drawer.
    if (context === "cart-page") {
      const taxAmount = extractTaxFromPrice(priceInCents, config.rate);
      const formattedAmount = formatTax(taxAmount);
      block.innerHTML = `
        <span class="vat-display__row-label">${config.tax_name}:</span>
        <span class="vat-display__row-value vat-display__amount">${formattedAmount}</span>
      `;
      return;
    }

    const translation = getTranslation(locale);
    let displayText;

    if (isRange) {
      const taxAmountMin = extractTaxFromPrice(priceMin, config.rate);
      const taxAmountMax = extractTaxFromPrice(priceMax, config.rate);
      const formattedMin = formatTax(taxAmountMin);
      const formattedMax = formatTax(taxAmountMax);

      displayText = applyTranslation(translation.includes_tax_range || translation.includes_tax, {
        tax_name: config.tax_name,
        rate: Math.round(config.rate * 100),
        amount_min: boldAmount(formattedMin),
        amount_max: boldAmount(formattedMax),
        amount: boldAmount(formattedMin), // fallback if includes_tax_range is missing
      });
    } else {
      const taxAmount = extractTaxFromPrice(priceInCents, config.rate);
      const formattedAmount = formatTax(taxAmount);
      const templateKey = context === "cart" ? "cart_tax_total" : "includes_tax";

      displayText = applyTranslation(translation[templateKey], {
        tax_name: config.tax_name,
        rate: Math.round(config.rate * 100),
        amount: boldAmount(formattedAmount),
      });
    }

    // The translated sentence is rendered as a data attribute by
    // snippets/vat-display.liquid, which picks the English default itself when the
    // store has not received the locale key yet. Which of the two applies follows the
    // country's tax, so that decision stays in TAX_CONFIG. The English tooltip there
    // is the last resort, for a block rendered before this attribute existed.
    const translatedTooltip = config.tax_name === "GST" ? block.dataset.tooltipGst : block.dataset.tooltipVat;
    const tooltip = escapeHtml(translatedTooltip || config.tooltip || "");

    // Remove any tooltip this block previously portaled to <body> before
    // rebuilding, so re-renders (e.g. variant change) don't leave orphans.
    const staleTip = document.getElementById(`${block.id}-tt`);
    if (staleTip) {
      staleTip.remove();
    }

    block.innerHTML = `
      <span class="vat-display__text">
        ${displayText}
        <span class="vat-display__info">
          <button
            type="button"
            class="vat-display__icon"
            aria-label="${tooltip}"
            aria-expanded="false"
          >${INFO_ICON_SVG}</button>
          <span class="vat-display__tooltip" id="${block.id}-tt" role="tooltip">
            ${tooltip}
          </span>
        </span>
      </span>
    `;

    initTooltip(block);
  }

  // ---------------------------------------------------
  // Cart Drawer Observer
  // ---------------------------------------------------

  let isRendering = false;
  let renderTimeout = null;

  /**
   * Attempts to render VAT display blocks.
   * Prevents concurrent renders via isRendering flag.
   * Requires window.renderVatDisplayBlocks to be defined.
   *
   * @returns {void}
   */
  function tryRenderVat() {
    if (typeof window.renderVatDisplayBlocks !== "function") {
      return;
    }

    // Allow re-render even during lock if any VAT block is empty
    // Handles DOM morphing after cart quantity updates
    const hasEmptyBlock = Array.from(document.querySelectorAll(".vat-display")).some(function (block) {
      return block.innerHTML.trim() === "";
    });

    if (isRendering && !hasEmptyBlock) {
      return;
    }

    isRendering = true;
    renderWithRetry(3, 300);
    setTimeout(function () {
      isRendering = false;
    }, 1500);
  }

  /**
   * Debounced render to prevent rapid-fire calls.
   * Cancels any pending render before scheduling a new one.
   *
   * @param {number} [delay=150] - Delay in milliseconds before rendering
   * @returns {void}
   */
  function debouncedRender(delay) {
    if (renderTimeout) {
      clearTimeout(renderTimeout);
    }
    renderTimeout = setTimeout(function () {
      tryRenderVat();
    }, delay || 150);
  }

  /**
   * Initialises all cart drawer watchers and event listeners.
   * Uses multiple methods to support different theme implementations:
   *   1. MutationObserver on cart summary element
   *   2. Cart panel attribute observer for visibility changes
   *   3. Fetch interceptor for cart API calls
   *   4. Click event listener for cart icon and quantity buttons
   *   5. Standard Shopify cart events
   *
   * @returns {void}
   */
  function watchForDrawer() {
    // Watch cart summary element for DOM changes
    const cartSummary = document.querySelector(".cart-drawer__summary, .cart-totals");
    if (cartSummary) {
      new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
            debouncedRender(400);
          }
        });
      }).observe(cartSummary.parentElement || document.body, {
        childList: true,
        subtree: false,
      });
    }

    // Watch cart totals container for DOM morphing after quantity updates
    const cartTotalsContainer = document.querySelector(".cart-totals__container");
    if (cartTotalsContainer) {
      new MutationObserver(function () {
        const drawerBlock = document.getElementById("vat-display-cart-cart-drawer");
        if (drawerBlock && drawerBlock.innerHTML.trim() === "") {
          debouncedRender(200);
        }
      }).observe(cartTotalsContainer, {
        childList: true,
        subtree: true,
        attributes: false,
      });
    }

    // Watch the cart page summary card for DOM morphing after quantity
    // updates (the morph reconciles against the server-rendered empty
    // placeholder, wiping the injected row). Guarded on the block being
    // empty — same pattern as the drawer observer above — so re-injecting
    // the row never re-triggers this observer in a loop.
    const cartPageVatBlock = document.getElementById("vat-display-cart-page-summary");
    if (cartPageVatBlock && cartPageVatBlock.parentElement) {
      new MutationObserver(function () {
        const block = document.getElementById("vat-display-cart-page-summary");
        if (block && block.innerHTML.trim() === "") {
          debouncedRender(200);
        }
      }).observe(cartPageVatBlock.parentElement, {
        childList: true,
        subtree: true,
        attributes: false,
      });
    }

    // Watch cart panel visibility changes via attribute observer
    const cartPanel = document.querySelector('[data-cart-drawer], .cart-drawer, [aria-label="Cart"], [id*="cart"]');
    if (cartPanel) {
      new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          if (
            mutation.type === "attributes" &&
            (mutation.attributeName === "aria-hidden" ||
              mutation.attributeName === "open" ||
              mutation.attributeName === "class")
          ) {
            debouncedRender(500);
          }
        });
      }).observe(cartPanel, {
        attributes: true,
        childList: false,
        subtree: false,
      });
    }

    // Intercept fetch calls for cart operations
    const originalFetch = window.fetch;
    window.fetch = function () {
      const url = arguments[0];
      if (
        typeof url === "string" &&
        (url.includes("/cart/change") || url.includes("/cart/add") || url.includes("/cart/update"))
      ) {
        return originalFetch.apply(this, arguments).then(function (response) {
          debouncedRender(300); // ← reduced from 600
          return response;
        });
      }
      return originalFetch.apply(this, arguments);
    };

    // Click listeners for cart icon and quantity buttons
    document.addEventListener("click", function (event) {
      const cartTrigger = event.target.closest(
        '[href$="/cart"], [data-cart-toggle], ' +
        '.cart-count-bubble, [aria-controls="cart-drawer"], ' +
        "[data-open-cart], .header__icon--cart"
      );
      if (cartTrigger) {
        debouncedRender(600);
      }

      const quantityBtn = event.target.closest(
        '.quantity__button, [name="minus"], [name="plus"], ' +
        "cart-remove-button button, [data-quantity-minus], " +
        "[data-quantity-plus]"
      );
      if (quantityBtn) {
        debouncedRender(1000);
      }
    });

    // Watch for quantity input changes
    document.addEventListener("input", function (event) {
      const quantityInput = event.target.closest('.quantity__input, [data-quantity-input], input[name="updates[]"]');
      if (quantityInput) {
        debouncedRender(800);
      }
    });

    // Standard cart events
    document.addEventListener("cart:update", function () {
      debouncedRender(400);
    });
    document.addEventListener("cart:refresh", function () {
      debouncedRender(400);
    });
    document.addEventListener("cart:open", function () {
      debouncedRender(400);
    });
    document.addEventListener("drawer:opened", function () {
      debouncedRender(400);
    });
  }

  /**
   * Finds all VAT display blocks on the page and renders them.
   * Exposed globally so cart drawer observer can call it
   * after AJAX cart updates.
   *
   * @returns {void}
   */
  window.renderVatDisplayBlocks = function () {
    const blocks = document.querySelectorAll(".vat-display");
    blocks.forEach(renderBlock);
  };

  // ---------------------------------------------------
  // Variant Change Handler
  // ---------------------------------------------------

  /**
   * Updates product-context VAT display blocks with a new variant price.
   * Called when the customer selects a different product variant.
   * Clears range attributes so the display collapses from a range
   * to a single tax amount.
   *
   * Only touches blocks with data-context="product" — cart and
   * cart-page blocks are left untouched.
   *
   * @param {number} newPriceInCents - New variant price in cents
   * @returns {void}
   */
  function updateVatForVariant(newPriceInCents) {
    const blocks = document.querySelectorAll('.vat-display[data-context="product"]');
    blocks.forEach(block => {
      // Clear range attributes so renderBlock falls through to single-price logic
      delete block.dataset.priceMin;
      delete block.dataset.priceMax;
      block.dataset.price = newPriceInCents;
    });
    window.renderVatDisplayBlocks();
  }

  /**
   * Safely fetches variant data by ID and updates VAT display.
   * Validates HTTP response status before JSON parsing to prevent
   * SyntaxError on 404 responses or empty bodies.
   *
   * @param {string} variantId - Shopify variant ID
   * @returns {void}
   */
  function fetchVariantAndUpdate(variantId) {
    fetch(`/variants/${variantId}.js`)
      .then(function (response) {
        if (!response.ok) {
          throw new Error(`Variant ${variantId} not found (${response.status})`);
        }
        return response.json();
      })
      .then(function (variant) {
        if (variant && variant.price) {
          updateVatForVariant(variant.price);
        }
      })
      .catch(function (e) {
        console.warn("VAT Display: Variant fetch failed", e);
      });
  }

  /**
   * Initialises all variant change event listeners.
   *
   * Primary method uses the custom theme 'variant:update' event
   * which provides full variant data including price directly.
   * Fallback methods handle other theme implementations.
   *
   * Methods:
   *   1. variant:update  - Custom theme event (primary)
   *                        event.detail.resource.price
   *   2. variant:changed - Standard Shopify event
   *                        event.detail.variant.price
   *   3. variantChange   - Some custom theme event
   *                        event.detail.variant.price
   *
   * @returns {void}
   */
  function initVariantChangeListeners() {
    // Method 1: Custom theme event - primary handler
    // Provides full variant object via event.detail.resource
    document.addEventListener("variant:update", event => {
      const variant = event.detail?.resource;
      if (!variant) {
        return;
      }
      const price = parseInt(variant.price, 10);
      if (price) {
        updateVatForVariant(price);
      }
    });

    // Method 2: Standard Shopify variant change event
    document.addEventListener("variant:changed", event => {
      const variant = event.detail?.variant;
      if (variant && variant.price) {
        updateVatForVariant(variant.price);
      }
    });

    // Method 3: Custom event used by some themes
    document.addEventListener("variantChange", event => {
      const variant = event.detail?.variant;
      if (variant && variant.price) {
        updateVatForVariant(variant.price);
      }
    });
  }

  // ---------------------------------------------------
  // Initialise
  // ---------------------------------------------------

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      watchForDrawer();
      window.renderVatDisplayBlocks();
      initVariantChangeListeners();
    });
  } else {
    watchForDrawer();
    window.renderVatDisplayBlocks();
    initVariantChangeListeners();
  }
} // end vatDisplayInitialized guard
